import { MouseEvent } from 'react';
import Image from 'next/image';

import { Box, Flex, Grid, Heading, Skeleton, Text, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { BreakpointValue } from '@udacity/chakra-uds-theme';

import { Modal } from '~/components/modal';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { CatalogCard } from '~/features/experiments/ab_plp_redesign_2025/components/catalog-card';
import { AcademicDegreeTypeIcon, StopWatchIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export interface WoolfProgram {
  key: string;
  title: string;
  image: string | null;
  semanticType: 'Woolf_Program';
  rawDurationDisplay: string;
}

interface WoolfProgramCardProps {
  program: WoolfProgram;
  accentColor: string;
  imageOverlayColor: string;
  relatedUdacityPrograms?: CatalogItem[];
  modalAlertText?: string;
  width?: string | BreakpointValue<string>;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

export function WoolfProgramCard({
  program,
  accentColor,
  imageOverlayColor,
  relatedUdacityPrograms = [],
  modalAlertText,
  width,
  onClick,
}: WoolfProgramCardProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isClickable = relatedUdacityPrograms.length > 0;
  const modalSize = useBreakpointValue({ base: 'full', md: '4xl' });

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    onClick?.(e);
    if (isClickable) {
      onOpen();
    }
  }

  return (
    <>
      <Flex
        as='article'
        bg='white'
        border='1px solid'
        borderColor='grey.100'
        borderRadius='8px'
        cursor={isClickable ? 'pointer' : 'default'}
        flexDirection='column'
        overflow='hidden'
        width={width}
        onClick={handleClick}
      >
        <Flex
          bgColor='#0A0B0F'
          borderRadius={{ base: '4px', md: '8px' }}
          flex={{ base: '0 0 78px', md: '0 0 120px' }}
          m={{ base: '6px', md: '8px' }}
          overflow='hidden'
          position='relative'
        >
          {program.image && (
            <Image
              alt=''
              quality={75}
              sizes='360px'
              src={program.image}
              style={{ objectFit: 'cover', pointerEvents: 'none' }}
              fill
            />
          )}

          <Box bg={imageOverlayColor} inset={0} mixBlendMode='color' position='absolute' zIndex={1} />

          <Box
            background='linear-gradient(90deg, #0B0B0B 0%, rgba(156, 13, 34, 0) 100%), linear-gradient(284.04deg, rgba(0, 0, 0, 0.64) 0%, rgba(0, 0, 0, 0) 100%)'
            inset={0}
            opacity={0.64}
            position='absolute'
            zIndex={2}
          />

          <Flex
            alignItems='center'
            bg={accentColor}
            borderRadius='4px'
            bottom='8px'
            h={{ base: '28px', md: '40px' }}
            justifyContent='center'
            left='8px'
            position='absolute'
            w={{ base: '28px', md: '40px' }}
            zIndex={3}
          >
            <AcademicDegreeTypeIcon
              color='white'
              height={{ base: '16px', md: '22px' }}
              width={{ base: '16px', md: '22px' }}
            />
          </Flex>
        </Flex>

        <Flex
          flex={1}
          flexDir='column'
          gap={{ base: '10px', md: '12px' }}
          pb={{ base: 2, md: 3 }}
          pt={{ base: '10px', md: 4 }}
          px={{ base: 2, md: 3 }}
        >
          <Box noOfLines={4}>
            <Heading
              as='span'
              color='grey.900'
              display='block'
              fontFamily='body'
              fontSize={{ base: '0.75rem', md: '0.9375rem' }}
              fontWeight='600'
              lineHeight='1.4'
              mb={2}
            >
              {program.title}
            </Heading>
          </Box>

          {relatedUdacityPrograms.length > 0 && (
            <Text fontSize='xs'>
              <Box as='span' color='grey.500'>
                Included Udacity Programs:{' '}
              </Box>
              <Box as='span' color={accentColor} fontWeight='500'>
                {relatedUdacityPrograms.map((p) => p.title).join(', ')}
              </Box>
            </Text>
          )}

          <Box borderColor='grey.100 !important' borderTop='1px dashed' mt='auto' />

          <Flex alignItems='center' gap='8px' justifyContent='space-between'>
            <Flex alignItems='center' color={accentColor} gap='6px'>
              <AcademicDegreeTypeIcon height={{ base: '12px', md: '16px' }} width={{ base: '12px', md: '16px' }} />
              <Text
                fontFamily='condensed'
                fontSize={{ base: '0.625rem', md: '0.8125rem' }}
                fontWeight='500'
                letterSpacing='5%'
                lineHeight='1'
                noOfLines={1}
                textTransform='uppercase'
              >
                Master&apos;s Course
              </Text>
            </Flex>

            <Flex alignItems='center' color='grey.500' gap='6px'>
              <StopWatchIcon height={{ base: '12px', md: '16px' }} width={{ base: '12px', md: '16px' }} />
              <Text
                fontSize={{ base: '0.625rem', md: '0.75rem' }}
                lineHeight='1.2'
                noOfLines={1}
                textTransform='lowercase'
                wordBreak='break-all'
              >
                {program.rawDurationDisplay}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>

      {isClickable && (
        <Modal
          isOpen={isOpen}
          size={modalSize}
          title={
            <Flex alignItems='center' gap={{ base: '10px', md: '12px' }}>
              <Flex
                alignItems='center'
                bg={accentColor}
                borderRadius={{ base: '6px', md: '8px' }}
                flexShrink={0}
                h={{ base: '36px', md: '44px' }}
                justifyContent='center'
                w={{ base: '36px', md: '44px' }}
              >
                <AcademicDegreeTypeIcon
                  color='white'
                  height={{ base: '20px', md: '24px' }}
                  width={{ base: '20px', md: '24px' }}
                />
              </Flex>
              <Box>
                <Heading as='h2' fontSize={{ base: 'md', md: 'lg' }} fontWeight='700' lineHeight='1.3'>
                  {program.title}
                </Heading>
                <Flex
                  alignItems='center'
                  color='grey.500'
                  columnGap='8px'
                  flexWrap='wrap'
                  fontSize='sm'
                  mt='4px'
                  rowGap='4px'
                >
                  <Flex alignItems='center' gap='8px'>
                    <Text
                      fontFamily='condensed'
                      fontSize='sm'
                      fontWeight='500'
                      letterSpacing='0.05em'
                      lineHeight='1'
                      textTransform='uppercase'
                    >
                      Master&apos;s Course
                    </Text>
                    <Text>·</Text>
                  </Flex>
                  <Flex alignItems='center' gap='6px'>
                    <StopWatchIcon flexShrink={0} height='12px' width='12px' />
                    <Text fontFamily='body' fontSize='xs'>
                      {program.rawDurationDisplay}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
            </Flex>
          }
          onClose={onClose}
        >
          <Flex flexDir='column' gap={{ base: 4, md: 5 }} pb={4} pt={2}>
            <Box>
              <Text
                color={accentColor}
                fontFamily='condensed'
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight='500'
                letterSpacing='0.08em'
                mb={3}
                textTransform='uppercase'
              >
                Included Udacity Programs
              </Text>

              <Flex
                bg='grey.50'
                borderLeft='3px solid'
                borderLeftColor='grey.300'
                borderRadius='0 6px 6px 0'
                mb={4}
                px={{ base: 3, md: 4 }}
                py={{ base: 2, md: 3 }}
              >
                <Text color='grey.700' fontSize={{ base: 'xs', md: 'sm' }} lineHeight='1.5'>
                  {modalAlertText}
                </Text>
              </Flex>

              <Grid
                gap={{ base: '12px', md: '16px' }}
                templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(3, 246px)' }}
              >
                {relatedUdacityPrograms.map((catalogItem) => (
                  <CatalogCard key={catalogItem.key} catalogItem={catalogItem} showDuration={false} width='100%' />
                ))}
              </Grid>
            </Box>
          </Flex>
        </Modal>
      )}
    </>
  );
}

export function WoolfProgramCardSkeleton({ width = '190px' }: { width?: string | BreakpointValue<string> }) {
  return (
    <Flex
      bg='white'
      border='1px solid'
      borderColor='grey.100'
      borderRadius='8px'
      flexDirection='column'
      overflow='hidden'
      width={width}
    >
      <Skeleton
        borderRadius={{ base: '4px', md: '8px' }}
        flex={{ base: '0 0 78px', md: '0 0 120px' }}
        m={{ base: '6px', md: '8px' }}
      />
      <Flex flex={1} flexDir='column' gap='10px' p='12px' pt='10px'>
        <Skeleton h='14px' w='90%' />
        <Skeleton h='14px' w='70%' />
        <Skeleton h='10px' w='100%' />
        <Skeleton h='10px' w='80%' />
        <Flex justifyContent='space-between' mt='auto'>
          <Skeleton h='12px' w='80px' />
          <Skeleton h='12px' w='60px' />
        </Flex>
        <Box borderColor='grey.100 !important' borderTop='1px dashed' />
        <Skeleton h='10px' w='60%' />
        <Skeleton h='10px' w='40%' />
      </Flex>
    </Flex>
  );
}
